// ─── Sound Manager — synthesized sounds via Web Audio API ────────────
class SoundManager {
  private ctx: AudioContext | null = null;
  private enabled = true;

  private getCtx(): AudioContext {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
    return this.ctx;
  }

  private tone(freq: number, duration: number, type: OscillatorType = 'sine', vol = 0.15) {
    if (!this.enabled) return;
    try {
      const ctx = this.getCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      gain.gain.setValueAtTime(vol, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + duration);
    } catch {}
  }

  match(count: number) {
    const base = 520 + count * 60;
    this.tone(base, 0.12, 'sine', 0.12);
    setTimeout(() => this.tone(base * 1.25, 0.1, 'sine', 0.1), 60);
    if (count >= 3) setTimeout(() => this.tone(base * 1.5, 0.15, 'triangle', 0.08), 120);
  }

  swap() {
    this.tone(300, 0.06, 'sine', 0.06);
  }

  invalid() {
    this.tone(180, 0.15, 'square', 0.05);
  }

  bombExplode() {
    // Low rumble
    this.tone(80, 0.3, 'sawtooth', 0.2);
    setTimeout(() => this.tone(60, 0.25, 'square', 0.15), 50);
    setTimeout(() => this.tone(40, 0.2, 'sawtooth', 0.1), 100);
  }

  rocketLaunch() {
    this.tone(400, 0.08, 'sine', 0.1);
    setTimeout(() => this.tone(600, 0.08, 'sine', 0.1), 50);
    setTimeout(() => this.tone(900, 0.12, 'sine', 0.12), 100);
  }

  colorBomb() {
    [523, 659, 784, 1047].forEach((f, i) => {
      setTimeout(() => this.tone(f, 0.12, 'sine', 0.1), i * 60);
    });
  }

  drop(count: number) {
    for (let i = 0; i < Math.min(count, 5); i++) {
      setTimeout(() => this.tone(350 + i * 40, 0.05, 'sine', 0.04), i * 30);
    }
  }

  levelComplete() {
    [523, 659, 784, 1047, 1319].forEach((f, i) => {
      setTimeout(() => this.tone(f, 0.2, 'sine', 0.12), i * 100);
    });
  }

  gameOver() {
    [400, 350, 300, 250].forEach((f, i) => {
      setTimeout(() => this.tone(f, 0.25, 'triangle', 0.1), i * 150);
    });
  }

  select() {
    this.tone(440, 0.06, 'sine', 0.06);
  }

  setEnabled(on: boolean) { this.enabled = on; }
}

export const soundManager = new SoundManager();
